import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSyncedPollingSubscriber} from '../../state/polling/hooks';
import {Query} from '../../state/runs/types';
import {BucketedQueryManager} from '../PanelRunsLinePlot/bucketedData/bucketedQueryManager';
import {Range} from '../PanelRunsLinePlot/common';
import {Zoom} from '../PanelRunsLinePlot/types';

export interface SharedPanelQueryState {
  query: Query | null;
  result: any;
}

interface SharedPanelStateContextType {
  queryState: SharedPanelQueryState;
  setQueryState: (queryState: SharedPanelQueryState) => void;
  userZoomState: Zoom;
  setUserZoomState: React.Dispatch<React.SetStateAction<Zoom>>;
  xDomainQuery: Range;
  setXDomainQuery: (xDomainQuery: Range) => void;
  bucketedQueryManager: BucketedQueryManager;
}

export const SharedPanelStateContext =
  createContext<SharedPanelStateContextType | null>(null);

export const SharedPanelStateContextProvider: React.FC = ({children}) => {
  const [queryState, setQueryState] = useState<SharedPanelQueryState>({
    query: null,
    result: null,
  });
  const [userZoomState, setUserZoomState] = useState<Zoom>({});
  const [xDomainQuery, setXDomainQuery] = useState<Range>({
    min: null,
    max: null,
  });
  const [bucketedQueryManager, setBucketedQueryManager] =
    useState<BucketedQueryManager>(() => new BucketedQueryManager());
  const {lastTickedAt, stopTaskTimer} = useSyncedPollingSubscriber(
    'BucketedQueryManager'
  );
  useEffect(() => {
    bucketedQueryManager.poll();
  }, [bucketedQueryManager, lastTickedAt]);

  const value = useMemo(
    () => ({
      queryState,
      setQueryState,
      userZoomState,
      setUserZoomState,
      xDomainQuery,
      setXDomainQuery,
      bucketedQueryManager,
    }),
    [queryState, userZoomState, xDomainQuery, bucketedQueryManager]
  );

  return (
    <SharedPanelStateContext.Provider value={value}>
      {children}
    </SharedPanelStateContext.Provider>
  );
};

export const useSharedPanelState = (): SharedPanelStateContextType => {
  const ctx = useContext(SharedPanelStateContext);
  if (ctx == null) {
    throw new Error(
      'useSharedPanelState must be used within a SharedPanelStateContextProvider'
    );
  }
  return ctx;
};
